import { FC, useEffect, useState } from 'react';

import {
  AvailableInventoryResponse,
  closeExtraRedeem,
  getCouponAvailableInventory,
  openExtraRedeem,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';

import { fmt } from './constants';

interface ExtraRedeemModalProps {
  coupon: GiftCouponSummary;
  onClose: () => void;
  /** 열기/닫기 성공 시 호출 — 부모가 목록을 갱신한다 */
  onChanged: () => void;
}

// date input(yyyy-MM-dd) → KST 그날 23:59:59 ISO
const toExpiresIso = (dateStr: string): string =>
  new Date(`${dateStr}T23:59:59+09:00`).toISOString();

// ISO/Date → date input value(yyyy-MM-dd)
const toDateInputValue = (d?: Date | string): string => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const ExtraRedeemModal: FC<ExtraRedeemModalProps> = ({
  coupon,
  onClose,
  onChanged,
}) => {
  const existing = coupon.extraRedeem;
  const used = existing?.used ?? 0;
  const isOpen = !!existing;

  const [inventory, setInventory] = useState<AvailableInventoryResponse | null>(null);
  const [loadingInventory, setLoadingInventory] = useState(true);

  const [allowed, setAllowed] = useState<number>(existing?.allowed ?? 1);
  const [reason, setReason] = useState<string>(existing?.reason ?? '');
  const [expiresAt, setExpiresAt] = useState<string>(toDateInputValue(existing?.expiresAt));

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getCouponAvailableInventory(coupon._id)
      .then((res) => {
        if (alive) setInventory(res);
      })
      .catch((err) => {
        console.error('충당 재고 조회 실패:', err);
        if (alive) setInventory(null);
      })
      .finally(() => {
        if (alive) setLoadingInventory(false);
      });
    return () => {
      alive = false;
    };
  }, [coupon._id]);

  const minAllowed = Math.max(1, used);

  const handleOpen = async () => {
    if (allowed < minAllowed) {
      setError(`허용 건수는 이미 사용된 ${used}건 이상이어야 합니다.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await openExtraRedeem(coupon._id, {
        allowed,
        reason: reason.trim() || undefined,
        expiresAt: expiresAt ? toExpiresIso(expiresAt) : undefined,
      });
      onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '추가교환 열기에 실패했습니다.');
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (
      !confirm(
        '추가교환을 닫으시겠습니까?\n이후 추가 수령 신청을 받을 수 없습니다. (이미 신청된 건은 유지됩니다)',
      )
    )
      return;
    setSubmitting(true);
    setError(null);
    try {
      await closeExtraRedeem(coupon._id);
      onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '추가교환 닫기에 실패했습니다.');
      setSubmitting(false);
    }
  };

  const availableCount = inventory?.availableCount ?? 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex w-full max-w-md flex-col gap-4 rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-violet-800">
              중복 교환 허용 (추가교환)
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              #{coupon.seq} · 코드 {coupon.code}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* 현재 상태 */}
        {isOpen && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium text-violet-800">현재 열림</span>
              <span className="text-violet-700">
                허용 {existing?.allowed} · 사용 {used} · 잔여{' '}
                <span className="font-bold">
                  {Math.max(0, (existing?.allowed ?? 0) - used)}
                </span>
              </span>
            </div>
            <p className="mt-1 text-xs text-violet-500">
              신청 기한:{' '}
              {existing?.expiresAt ? `${fmt(existing.expiresAt)}까지` : '무기한'}
            </p>
          </div>
        )}

        {/* 충당 가능 재고 */}
        <div
          className={`rounded-lg border px-3 py-2.5 text-sm ${
            availableCount > 0
              ? 'border-emerald-200 bg-emerald-50'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          {loadingInventory ? (
            <span className="text-gray-500">재고 확인 중…</span>
          ) : availableCount > 0 ? (
            <span className="text-emerald-700">
              충당 가능 재고(같은 주문·상품 미전달){' '}
              <span className="font-bold">{availableCount}건</span> — 카드 없이 대체발송
              가능
            </span>
          ) : (
            <span className="text-amber-700">
              충당 가능 재고 없음 — 추가결제(빌링카드)로만 처리 가능
            </span>
          )}
        </div>

        {/* 열기 / 수정 폼 */}
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-gray-700">허용 건수</span>
            <input
              type="number"
              min={minAllowed}
              value={allowed}
              onChange={(e) =>
                setAllowed(Math.max(minAllowed, Number(e.target.value) || minAllowed))
              }
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            {used > 0 && (
              <span className="mt-0.5 block text-xs text-gray-400">
                이미 {used}건 신청됨 — 그보다 작게 줄일 수 없습니다.
              </span>
            )}
          </label>

          <label className="block text-sm">
            <span className="font-medium text-gray-700">사유 (선택)</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="예: 카톡 중복 전달로 2명 추가 수령"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-gray-700">신청 기한 (선택)</span>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            <span className="mt-0.5 block text-xs text-gray-400">
              미지정 시 무기한으로 열립니다.
            </span>
          </label>
        </div>

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          {isOpen ? (
            <button
              onClick={handleClose}
              disabled={submitting}
              className="rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              추가교환 닫기
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={handleOpen}
            disabled={submitting}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {submitting ? '처리중…' : isOpen ? '허용 건수 수정' : '추가교환 열기'}
          </button>
        </div>
      </div>
    </div>
  );
};
